import React, { useState, useEffect } from 'react';
import {
  Typography,
  TextField,
  Box,
  Button,
  IconButton,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  FactoryOutlined as FactoryIcon,
} from '@mui/icons-material';

import type { ContractFactorySectionProps, PropertyCheckMethod } from '../../types/property.types';

import { commonButtonStyles } from '@/components/styles/common-style';
import { CHECK_METHOD_OPTIONS, PURCHASE_ORDER_OPTIONS } from '@/lib/constants';
import { getStylesForEditableField } from '@/utils/helpers';
import { PaperBoxStyles } from '@/components/styles/common-style';

export const ContractFactorySection: React.FC<ContractFactorySectionProps> = ({
  formData,
  originalFormData,
  onUpdateField,
  onAddContractFactory,
  onUpdateContractFactory,
  onRemoveContractFactory,
  getFieldError,
  isFieldValid,
  canManagePrice,
}) => {
  const maxFactoryMembers = 10;
  const currentCount = formData.contract_factories?.length || 0;
  const canAddMore = currentCount < maxFactoryMembers;
  const [purchaseOrderValue, setPurchaseOrderValue] = useState<string>('');
  const [commonCementValue, setCommonCementValue] = useState<number | undefined>(undefined);

  // Extract purchase order value for Select component
  useEffect(() => {
    if (formData.exists_purchase_order === undefined || formData.exists_purchase_order === null) {
      setPurchaseOrderValue('');
    } else if (formData.exists_purchase_order) {
      setPurchaseOrderValue('true');
    } else {
      setPurchaseOrderValue('false');
    }
  }, [formData.exists_purchase_order]);

  // Extract common cement value for Select component
  useEffect(() => {
    if (formData.link_cement_code === undefined || formData.link_cement_code === null) {
      setCommonCementValue(undefined);
    } else if (formData.link_cement_code === 1) {
      setCommonCementValue(1);
    } else if (formData.link_cement_code === 2) {
      setCommonCementValue(2);
    } else {
      setCommonCementValue(undefined);
    }
  }, [formData.link_cement_code]);

  return (
    <PaperBoxStyles elevation={1} sx={{ mb: 3, paddingBottom: '32px' }}>
      {/* 契約工場 Section */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant='h5'
          fontWeight='bold'
          sx={{
            mb: 2,
            color: '#1A1C20',
            fontSize: '22px',
            fontWeight: 600,
          }}
        >
          契約工場
        </Typography>

        <Typography variant='body2' color='text.secondary' sx={{ mb: 3, fontSize: '14px' }}>
          契約工場追加
        </Typography>

        {/* Factory List */}
        {formData.contract_factories && formData.contract_factories.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            {formData.contract_factories.map((factory, index) => {
              const factoryId = 'id' in factory ? factory.id : index;
              const originalFactoryData =
                factory.index !== undefined
                  ? originalFormData?.contract_factories?.[factory.index]
                  : undefined;
              return (
                <Card
                  key={String(factoryId)}
                  sx={{ border: '1px solid #E0E0E0', backgroundColor: '#FFFFFF' }}
                >
                  <CardHeader
                    avatar={<FactoryIcon sx={{ color: '#1976d2' }} />}
                    title={`工場 #${index + 1}`}
                    action={
                      <IconButton
                        onClick={() => onRemoveContractFactory(factoryId)}
                        sx={{ color: '#f44336' }}
                        size='small'
                      >
                        <DeleteIcon />
                      </IconButton>
                    }
                    sx={{ pb: 1 }}
                  />
                  <CardContent sx={{ pt: 0 }}>
                    <Box
                      sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}
                    >
                      <TextField
                        fullWidth
                        label='工場'
                        placeholder='工場を入力'
                        value={factory.factory}
                        onChange={(e) =>
                          onUpdateContractFactory(factoryId, 'factory', e.target.value)
                        }
                        error={!isFieldValid(`contract_factories.${index}.factory`)}
                        helperText={getFieldError(`contract_factories.${index}.factory`)}
                        slotProps={{
                          input: {
                            inputProps: { maxLength: 100 },
                            sx: getStylesForEditableField(
                              !!originalFormData,
                              originalFactoryData?.factory,
                              factory.factory
                            ),
                          },
                        }}
                      />
                      <TextField
                        fullWidth
                        label='納入区分'
                        placeholder='納入区分を入力'
                        value={factory.delivery_classification}
                        onChange={(e) =>
                          onUpdateContractFactory(
                            factoryId,
                            'delivery_classification',
                            e.target.value
                          )
                        }
                        error={!isFieldValid(`contract_factories.${index}.delivery_classification`)}
                        helperText={getFieldError(
                          `contract_factories.${index}.delivery_classification`
                        )}
                        slotProps={{
                          input: {
                            inputProps: { maxLength: 50 },
                            sx: getStylesForEditableField(
                              !!originalFormData,
                              originalFactoryData?.delivery_classification,
                              factory.delivery_classification
                            ),
                          },
                        }}
                      />
                      <TextField
                        fullWidth
                        label='数量'
                        placeholder='数量を入力'
                        value={factory.quantity || ''}
                        onChange={(e) =>
                          onUpdateContractFactory(
                            factoryId,
                            'quantity',
                            Number.parseFloat(e.target.value) || 0
                          )
                        }
                        error={!isFieldValid(`contract_factories.${index}.quantity`)}
                        helperText={getFieldError(`contract_factories.${index}.quantity`)}
                        slotProps={{
                          input: {
                            inputProps: { pattern: '[0-9]*', inputMode: 'numeric', maxLength: 9 },
                            endAdornment: (
                              <Typography variant='body2' sx={{ color: 'text.secondary', mr: 1 }}>
                                m³
                              </Typography>
                            ),
                            sx: getStylesForEditableField(
                              !!originalFormData,
                              originalFactoryData?.quantity,
                              factory.quantity
                            ),
                          },
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        ) : (
          /* Empty State */
          <Box
            sx={{
              border: '2px dashed #E0E0E0',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              backgroundColor: '#FFFFFF',
              mb: 3,
            }}
          >
            <FactoryIcon sx={{ fontSize: 48, color: '#BDBDBD', mb: 2 }} />
            <Typography variant='body1' color='text.secondary' sx={{ mb: 2 }}>
              契約工場
            </Typography>
          </Box>
        )}

        {/* Add Factory Button */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant='outlined'
            startIcon={<AddIcon />}
            onClick={onAddContractFactory}
            disabled={!canAddMore}
            sx={{
              ...commonButtonStyles,
              px: 3,
              py: 1,
            }}
          >
            入力行追加 ({currentCount}/{maxFactoryMembers})
          </Button>
        </Box>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* 情報入力 Section */}
      <Box>
        <Typography
          variant='h5'
          fontWeight='bold'
          sx={{
            mb: 3,
            color: '#1A1C20',
            fontSize: '22px',
            fontWeight: 600,
          }}
        >
          情報入力
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* 特殊事情・注意事項 */}
          <TextField
            fullWidth
            multiline
            rows={3}
            label='特殊事情・注意事項'
            placeholder='特殊事情・注意事項を入力'
            value={formData.manager_comment || ''}
            onChange={(e) => onUpdateField('manager_comment', e.target.value)}
            error={!isFieldValid('manager_comment')}
            helperText={getFieldError('manager_comment')}
            slotProps={{
              input: {
                inputProps: { maxLength: 65000 },
                sx: getStylesForEditableField(
                  !!originalFormData,
                  originalFormData?.manager_comment,
                  formData.manager_comment
                ),
              },
            }}
          />

          {/* 実績確認会社 */}
          <TextField
            fullWidth
            label='実績確認会社'
            placeholder='実績確認会社を入力'
            value={formData.achievement_confirmation_company || ''}
            onChange={(e) => onUpdateField('achievement_confirmation_company', e.target.value)}
            error={!isFieldValid('achievement_confirmation_company')}
            helperText={getFieldError('achievement_confirmation_company')}
            slotProps={{
              input: {
                inputProps: { maxLength: 100 },
                sx: getStylesForEditableField(
                  !!originalFormData,
                  originalFormData?.achievement_confirmation_company,
                  formData.achievement_confirmation_company
                ),
              },
            }}
          />

          {/* 実績確認方法 */}
          <FormControl fullWidth error={!isFieldValid('check_method')}>
            <InputLabel>実績確認方法</InputLabel>
            <Select
              value={formData.check_method ? formData.check_method.toString() : ''}
              onChange={(e) =>
                onUpdateField(
                  'check_method',
                  e.target.value ? (Number(e.target.value) as PropertyCheckMethod) : ''
                )
              }
              label='実績確認方法'
              slotProps={{
                input: {
                  sx: getStylesForEditableField(
                    !!originalFormData,
                    originalFormData?.check_method,
                    formData.check_method
                  ),
                },
              }}
            >
              <MenuItem value=''>実績確認方法を選択</MenuItem>
              {CHECK_METHOD_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{getFieldError('check_method')}</FormHelperText>
          </FormControl>

          {/* 当社ープラント直接契約 */}
          <TextField
            fullWidth
            label='当社ープラント直接契約'
            placeholder='当社ープラント直接契約を入力'
            value={formData.nohara_direct || ''}
            onChange={(e) => onUpdateField('nohara_direct', e.target.value)}
            error={!isFieldValid('nohara_direct')}
            helperText={getFieldError('nohara_direct')}
            slotProps={{
              input: {
                inputProps: { maxLength: 50 },
                sx: getStylesForEditableField(
                  !!originalFormData,
                  originalFormData?.nohara_direct,
                  formData.nohara_direct
                ),
              },
            }}
          />

          {/* プラント窓口会社 */}
          <TextField
            fullWidth
            label='プラント窓口会社'
            placeholder='プラント窓口会社を入力'
            value={formData.p_window || ''}
            onChange={(e) => onUpdateField('p_window', e.target.value)}
            error={!isFieldValid('p_window')}
            helperText={getFieldError('p_window')}
            slotProps={{
              input: {
                inputProps: { maxLength: 50 },
                sx: getStylesForEditableField(
                  !!originalFormData,
                  originalFormData?.p_window,
                  formData.p_window
                ),
              },
            }}
          />

          {/* GC以外の販売先 */}
          <TextField
            fullWidth
            label='GC以外の販売先'
            placeholder='GC以外の販売先を入力'
            value={formData.sellers_other_gc || ''}
            onChange={(e) => onUpdateField('sellers_other_gc', e.target.value)}
            error={!isFieldValid('sellers_other_gc')}
            helperText={getFieldError('sellers_other_gc')}
            slotProps={{
              input: {
                inputProps: { maxLength: 50 },
                sx: getStylesForEditableField(
                  !!originalFormData,
                  originalFormData?.sellers_other_gc,
                  formData.sellers_other_gc
                ),
              },
            }}
          />

          {/* 備考 */}
          <TextField
            fullWidth
            multiline
            rows={3}
            label='備考'
            placeholder='備考を入力'
            value={formData.memo || ''}
            onChange={(e) => onUpdateField('memo', e.target.value)}
            error={!isFieldValid('memo')}
            helperText={getFieldError('memo')}
            slotProps={{
              input: {
                inputProps: { maxLength: 100 },
                sx: getStylesForEditableField(
                  !!originalFormData,
                  originalFormData?.memo,
                  formData.memo
                ),
              },
            }}
          />

          {/* 実績記入欄他注意事項 */}
          <TextField
            fullWidth
            multiline
            rows={3}
            label='実績記入欄他注意事項'
            placeholder='実績記入欄他注意事項を入力'
            value={formData.results_caution || ''}
            onChange={(e) => onUpdateField('results_caution', e.target.value)}
            error={!isFieldValid('results_caution')}
            helperText={getFieldError('results_caution')}
            slotProps={{
              input: {
                inputProps: { maxLength: 100 },
                sx: getStylesForEditableField(
                  !!originalFormData,
                  originalFormData?.results_caution,
                  formData.results_caution
                ),
              },
            }}
          />
          {canManagePrice && (
            <>
              {/* JV価格 & ゼネコン買付価格 */}
              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                <TextField
                  fullWidth
                  label='JV価格'
                  placeholder='JV価格を入力'
                  value={formData.jv_price || ''}
                  onChange={(e) => onUpdateField('jv_price', Number.parseInt(e.target.value) || 0)}
                  error={!isFieldValid('jv_price')}
                  helperText={getFieldError('jv_price')}
                  slotProps={{
                    input: {
                      inputProps: { maxLength: 9, pattern: '[0-9]*', inputMode: 'numeric' },
                      endAdornment: (
                        <Typography variant='body2' sx={{ color: 'text.secondary', mr: 1 }}>
                          円/m²
                        </Typography>
                      ),
                      sx: getStylesForEditableField(
                        !!originalFormData,
                        originalFormData?.jv_price,
                        formData.jv_price
                      ),
                    },
                  }}
                />
                <TextField
                  fullWidth
                  label='ゼネコン買付価格'
                  placeholder='ゼネコン買付価格を入力'
                  value={formData.general_contractor_purchase_price || ''}
                  onChange={(e) =>
                    onUpdateField(
                      'general_contractor_purchase_price',
                      Number.parseInt(e.target.value) || 0
                    )
                  }
                  error={!isFieldValid('general_contractor_purchase_price')}
                  helperText={getFieldError('general_contractor_purchase_price')}
                  slotProps={{
                    input: {
                      inputProps: { maxLength: 9, pattern: '[0-9]*', inputMode: 'numeric' },
                      endAdornment: (
                        <Typography variant='body2' sx={{ color: 'text.secondary', mr: 1 }}>
                          円/m²
                        </Typography>
                      ),
                      sx: getStylesForEditableField(
                        !!originalFormData,
                        originalFormData?.general_contractor_purchase_price,
                        formData.general_contractor_purchase_price
                      ),
                    },
                  }}
                />
              </Box>

              {/* 注文書 */}
              <FormControl fullWidth error={!isFieldValid('exists_purchase_order')}>
                <InputLabel>注文書</InputLabel>
                <Select
                  value={purchaseOrderValue}
                  onChange={(e) => {
                    const value = e.target.value as string;
                    onUpdateField(
                      'exists_purchase_order',
                      value === '' ? undefined : value === 'true'
                    );
                  }}
                  label='注文書'
                  slotProps={{
                    input: {
                      sx: getStylesForEditableField(
                        !!originalFormData,
                        originalFormData?.exists_purchase_order,
                        formData.exists_purchase_order
                      ),
                    },
                  }}
                >
                  <MenuItem value=''>注文書有無を選択</MenuItem>
                  {PURCHASE_ORDER_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{getFieldError('exists_purchase_order')}</FormHelperText>
              </FormControl>

              {/* 協同組合 */}
              <TextField
                fullWidth
                label='協同組合'
                placeholder='協同組合を入力'
                value={formData.cooperative || ''}
                onChange={(e) => onUpdateField('cooperative', e.target.value)}
                error={!isFieldValid('cooperative')}
                helperText={getFieldError('cooperative')}
                slotProps={{
                  input: {
                    inputProps: { maxLength: 50 },
                    sx: getStylesForEditableField(
                      !!originalFormData,
                      originalFormData?.cooperative,
                      formData.cooperative
                    ),
                  },
                }}
              />

              {/* リンクセメント */}
              <FormControl fullWidth error={!isFieldValid('link_cement_code')}>
                <InputLabel>リンクセメント</InputLabel>
                <Select
                  value={commonCementValue === undefined ? '' : commonCementValue}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === undefined) {
                      onUpdateField('link_cement_code', undefined);
                    } else {
                      onUpdateField('link_cement_code', Number(value));
                    }
                  }}
                  label='リンクセメント'
                  slotProps={{
                    input: {
                      sx: getStylesForEditableField(
                        !!originalFormData,
                        originalFormData?.link_cement_code,
                        formData.link_cement_code
                      ),
                    },
                  }}
                >
                  <MenuItem value=''>選択してください</MenuItem>
                  <MenuItem value={1}>対象</MenuItem>
                  <MenuItem value={2}>対象外</MenuItem>
                </Select>
                <FormHelperText>{getFieldError('link_cement_code')}</FormHelperText>
              </FormControl>
            </>
          )}
        </Box>
      </Box>
    </PaperBoxStyles>
  );
};
